import { z } from 'zod'

// Venue publication status
export type VenueStatus = 'draft' | 'published' | 'archived'

export const VenueStatusSchema = z.enum(['draft', 'published', 'archived'])

// Chat theme IDs
export type ChatThemeId = 'theme-modern' | 'theme-luxury' | 'theme-casual' | 'theme-nightlife' | 'theme-minimal'

export const ChatThemeIdSchema = z.enum(['theme-modern', 'theme-luxury', 'theme-casual', 'theme-nightlife', 'theme-minimal'])

// Theme definition for UI display
export interface ChatTheme {
  id: ChatThemeId
  name: string
  color: string // Tailwind class for preview swatch
}

// Available themes - used by UI components
export const CHAT_THEMES: ChatTheme[] = [
  { id: 'theme-modern', name: 'Modern', color: 'bg-cyan-200' },
  { id: 'theme-luxury', name: 'Luxury', color: 'bg-yellow-600' },
  { id: 'theme-casual', name: 'Casual', color: 'bg-orange-300' },
  { id: 'theme-nightlife', name: 'Nightlife', color: 'bg-purple-600' },
  { id: 'theme-minimal', name: 'Minimal', color: 'bg-gray-200' },
]

export const DEFAULT_CHAT_THEME: ChatThemeId = 'theme-modern'

// Speaking style IDs - defines the conversational tone of the AI concierge
export type SpeakingStyleId = 'style-professional' | 'style-friendly' | 'style-casual' | 'style-formal' | 'style-enthusiastic'

export const SpeakingStyleIdSchema = z.enum(['style-professional', 'style-friendly', 'style-casual', 'style-formal', 'style-enthusiastic'])

// Speaking style definition for UI display and prompt injection
export interface SpeakingStyle {
  id: SpeakingStyleId
  name: string
  description: string
  prompt: string // The actual prompt text to inject into system prompt
}

// Available speaking styles - used by UI components
// NOTE: The prompt text is stored in venue config when selected, so changing these
// presets won't affect existing venues (they keep their "frozen" prompt)
export const SPEAKING_STYLES: SpeakingStyle[] = [
  {
    id: 'style-professional',
    name: 'Professional',
    description: 'Balanced and business-like, efficient yet warm',
    prompt: `SPEAKING STYLE - Professional:
- Maintain a professional and courteous tone throughout the conversation
- Be helpful and efficient while remaining warm but not overly casual
- Use clear, concise language and avoid slang or colloquialisms
- Address guests respectfully and maintain appropriate boundaries
- Focus on providing accurate information and smooth service`
  },
  {
    id: 'style-friendly',
    name: 'Friendly',
    description: 'Warm and approachable, like a helpful neighbor',
    prompt: `SPEAKING STYLE - Friendly:
- Be warm, approachable, and genuinely helpful
- Use a conversational tone that makes guests feel welcome and at ease
- Feel free to use light humor when appropriate
- Show enthusiasm for helping guests have a great experience
- Be personable while still maintaining professionalism`
  },
  {
    id: 'style-casual',
    name: 'Casual',
    description: 'Relaxed and laid-back, easygoing vibe',
    prompt: `SPEAKING STYLE - Casual:
- Keep things relaxed and laid-back
- Use informal language and contractions naturally
- Be easygoing and don't over-explain things
- Match the energy of a friendly local spot
- Keep responses conversational and not overly structured`
  },
  {
    id: 'style-formal',
    name: 'Formal',
    description: 'Elegant and refined, fine dining experience',
    prompt: `SPEAKING STYLE - Formal:
- Maintain an elegant, refined, and sophisticated tone
- Use formal language appropriate for fine dining establishments
- Be attentive to detail and precise in your communication
- Address guests with the utmost respect and deference
- Convey exclusivity and exceptional service standards`
  },
  {
    id: 'style-enthusiastic',
    name: 'Enthusiastic',
    description: 'Energetic and excited, creates buzz',
    prompt: `SPEAKING STYLE - Enthusiastic:
- Be energetic and genuinely excited to help guests
- Show passion for the restaurant and its offerings
- Use expressive language that conveys enthusiasm
- Create a sense of excitement about the dining experience
- Be upbeat and positive while remaining helpful and informative`
  }
]

export const DEFAULT_SPEAKING_STYLE: SpeakingStyleId = 'style-professional'

// Helper to get the default speaking style prompt
export const getDefaultSpeakingStylePrompt = (): string => {
  const defaultStyle = SPEAKING_STYLES.find(s => s.id === DEFAULT_SPEAKING_STYLE)
  return defaultStyle?.prompt || SPEAKING_STYLES[0].prompt
}

// TTS Voice IDs - placeholder voice identifiers for text-to-speech
export type TTSVoiceId =
  | 'voice-none'
  | 'voice-tsumugi'
  | 'voice-ritsu'
  | 'voice-takehiro'
  | 'voice-kotaro'
  | 'voice-ryusei'
  | 'voice-mochiko'
  | 'voice-goki'

export const TTSVoiceIdSchema = z.enum([
  'voice-none',
  'voice-tsumugi',
  'voice-ritsu',
  'voice-takehiro',
  'voice-kotaro',
  'voice-ryusei',
  'voice-mochiko',
  'voice-goki'
])

// TTS Voice gender type
export type TTSVoiceGender = 'female' | 'male' | 'neutral'

// TTS Voice definition for UI display
export interface TTSVoice {
  id: TTSVoiceId
  name: string
  nameJa: string // Japanese name
  gender: TTSVoiceGender
  language: string
  provider: string // e.g., 'shs' for Shisa
  description: string
}

// Available TTS voices - placeholder list to be filled out later
export const TTS_VOICES: TTSVoice[] = [
  {
    id: 'voice-none',
    name: 'Disabled',
    nameJa: '無効',
    gender: 'neutral',
    language: 'N/A',
    provider: 'none',
    description: 'Text-to-speech is disabled for this venue.'
  },
  {
    id: 'voice-tsumugi',
    name: 'Tsumugi',
    nameJa: '春日部つむぎ - ノーマル',
    gender: 'female',
    language: 'Japanese',
    provider: 'shs',
    description: 'Cute and child-like female Japanese voice. Good for mascots and cartoon characters.'
  },
  {
    id: 'voice-ritsu',
    name: 'Ritsu',
    nameJa: '波音リツ - ノーマル',
    gender: 'female',
    language: 'Japanese',
    provider: 'shs',
    description: 'Formal female Japanese voice. Good for customer service and formal conversations.'
  },
  {
    id: 'voice-takehiro',
    name: 'Takehiro',
    nameJa: '玄野武宏 - ノーマル',
    gender: 'male',
    language: 'Japanese',
    provider: 'shs',
    description: 'Young male Japanese voice. Good for general purpose and casual conversation.'
  },
  {
    id: 'voice-kotaro',
    name: 'Kotaro',
    nameJa: '白上虎太郎 - ふつう',
    gender: 'neutral',
    language: 'Japanese',
    provider: 'shs',
    description: 'Cute neutral Japanese voice. Good for mascots and cartoon characters.'
  },
  {
    id: 'voice-ryusei',
    name: 'Ryusei',
    nameJa: '青山龍星 - ノーマル',
    gender: 'male',
    language: 'Japanese',
    provider: 'shs',
    description: 'Deeper male Japanese voice. Good for customer service and formal conversation.'
  },
  {
    id: 'voice-mochiko',
    name: 'Mochiko',
    nameJa: 'もち子さん - ノーマル',
    gender: 'female',
    language: 'Japanese',
    provider: 'shs',
    description: 'Sweet young female Japanese voice. Good for casual conversation and receptionist.'
  },
  {
    id: 'voice-goki',
    name: 'Goki',
    nameJa: '後鬼 - 人間ver.',
    gender: 'female',
    language: 'Japanese',
    provider: 'shs',
    description: 'Relaxed sweet mature female Japanese voice. Good for casual conversation and receptionist.'
  }
]

export const DEFAULT_TTS_VOICE: TTSVoiceId = 'voice-none'

// Default timezone (UTC as fallback)
export const DEFAULT_TIMEZONE = 'UTC'

// Helper to get browser's default timezone
export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return DEFAULT_TIMEZONE
  }
}

// Helper to get all available IANA timezones
export const getAvailableTimezones = (): string[] => {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch {
    // Fallback for older browsers
    return [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Asia/Tokyo',
      'Asia/Shanghai',
      'Asia/Singapore',
      'Australia/Sydney',
    ]
  }
}

// Helper to get UTC offset for a timezone (e.g., "+09:00", "-05:00")
export const getTimezoneOffset = (timezone: string): string => {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(now)
    const offsetPart = parts.find(p => p.type === 'timeZoneName')
    if (offsetPart) {
      // Convert "GMT+9" to "+09:00" format
      const match = offsetPart.value.match(/GMT([+-]?)(\d+)?(?::(\d+))?/)
      if (match) {
        const sign = match[1] || '+'
        const hours = match[2] ? match[2].padStart(2, '0') : '00'
        const minutes = match[3] ? match[3].padStart(2, '0') : '00'
        if (hours === '00' && minutes === '00') return 'UTC'
        return `${sign}${hours}:${minutes}`
      }
    }
    return ''
  } catch {
    return ''
  }
}

// Helper to format timezone with offset for display
export const formatTimezoneWithOffset = (timezone: string, label: string): string => {
  const offset = getTimezoneOffset(timezone)
  return offset ? `${label} (${offset})` : label
}

// Common timezones for quick selection (with display names)
export const COMMON_TIMEZONES: { value: string; label: string }[] = [
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Seoul', label: 'Seoul' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Asia/Bangkok', label: 'Bangkok' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Europe/Berlin', label: 'Berlin' },
  { value: 'America/New_York', label: 'New York' },
  { value: 'America/Chicago', label: 'Chicago' },
  { value: 'America/Denver', label: 'Denver' },
  { value: 'America/Los_Angeles', label: 'Los Angeles' },
  { value: 'Pacific/Honolulu', label: 'Honolulu' },
  { value: 'UTC', label: 'UTC' },
]

// Helper to get voice by ID
export const getTTSVoiceById = (id: TTSVoiceId): TTSVoice | undefined => {
  return TTS_VOICES.find(v => v.id === id)
}

// Days of the week
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'

// Time period for hours of operation
export interface TimePeriod {
  open: string // Format: "HH:MM" (24-hour)
  close: string // Format: "HH:MM" (24-hour)
}

// Hours for a specific day
export interface DayHours {
  day: DayOfWeek
  isClosed: boolean
  periods: TimePeriod[] // Support multiple periods per day (e.g., lunch and dinner)
}

// Photo/Image metadata
export interface VenuePhoto {
  id: string
  url: string
  filename: string
  caption?: string // Caption/description shown in carousel
  order: number // For drag-drop reordering
  hidden?: boolean // Hide from carousel without deleting
  uploadedAt: string
}

// RAG Document metadata
export interface VenueDocument {
  id: string
  url: string
  filename: string
  fileType: 'pdf' | 'txt' | 'text' | 'md' | 'csv'
  uploadedAt: string
  processed: boolean // Whether it's been processed for RAG
}

// RAG URL for web scraping
export interface VenueRagUrl {
  id: string
  url: string
  title?: string // Optional title/label for the URL
  addedAt: string
  lastScrapedAt?: string // When vault backend last scraped this URL
  status: 'pending' | 'active' | 'error' // Scraping status
  errorMessage?: string // Error message if status is 'error'
}

// Contact information
export interface VenueContact {
  phone?: string
  email?: string
  website?: string
}

// SNS Platform types
export type SNSPlatform = 'line' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'other'

export const SNSPlatformSchema = z.enum(['line', 'instagram', 'x', 'facebook', 'youtube', 'tiktok', 'other'])

// SNS Platform display info
export interface SNSPlatformInfo {
  id: SNSPlatform
  name: string
  placeholder: string
  urlPattern?: RegExp // For validation
  icon?: string
}

// Available SNS platforms
export const SNS_PLATFORMS: SNSPlatformInfo[] = [
  {
    id: 'line',
    name: 'LINE for Business',
    placeholder: 'https://line.me/R/ti/p/@yourstore or LINE ID',
    urlPattern: /^(https?:\/\/(line\.me|page\.line\.me|liff\.line\.me)\/|@[\w.-]+$)/,
  },
  {
    id: 'instagram',
    name: 'Instagram',
    placeholder: 'https://instagram.com/yourstore',
    urlPattern: /^https?:\/\/(www\.)?instagram\.com\//,
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    placeholder: 'https://x.com/yourstore',
    urlPattern: /^https?:\/\/(www\.)?(x\.com|twitter\.com)\//,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    placeholder: 'https://facebook.com/yourstore',
    urlPattern: /^https?:\/\/(www\.)?facebook\.com\//,
  },
  {
    id: 'youtube',
    name: 'YouTube',
    placeholder: 'https://youtube.com/@yourstore',
    urlPattern: /^https?:\/\/(www\.)?youtube\.com\//,
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    placeholder: 'https://tiktok.com/@yourstore',
    urlPattern: /^https?:\/\/(www\.)?tiktok\.com\//,
  },
  {
    id: 'other',
    name: 'Other',
    placeholder: 'https://example.com/yourprofile',
  },
]

// SNS Link entry
export interface VenueSNSLink {
  id: string
  platform: SNSPlatform
  url: string
  label?: string // Custom display label (e.g., "Official LINE Account")
}

export const VenueSNSLinkSchema = z.object({
  id: z.string(),
  platform: SNSPlatformSchema,
  url: z.string().min(1, 'URL is required'),
  label: z.string().max(100, 'Label too long').optional(),
})

// Helper to get platform info by ID
export const getSNSPlatformById = (id: SNSPlatform): SNSPlatformInfo | undefined => {
  return SNS_PLATFORMS.find(p => p.id === id)
}

// Address
export interface VenueAddress {
  street: string
  city: string
  state: string
  postalCode: string
  country: string
  latitude?: number
  longitude?: number
}

// LINE Bot integration status
export interface LineIntegration {
  linked: boolean
  linkedGroupIds?: string[]
  botDisplayName?: string
  linkedAt?: string
  linkedBy?: string
  responseMode?: 'all' | 'mention'
}

// Complete venue data
export interface Venue {
  id: string
  title: string
  description: string
  address: VenueAddress
  contact: VenueContact
  hours: DayHours[]
  photos: VenuePhoto[]
  documents: VenueDocument[]
  ragUrls?: VenueRagUrl[] // URLs for RAG web scraping
  snsLinks?: VenueSNSLink[] // Social media links (LINE, Instagram, X, etc.)
  vanitySlug: string // URL-friendly slug (e.g., "my-restaurant") - used for SEO, can change frequently
  status: VenueStatus // Publication status: draft, published, archived
  themeId?: ChatThemeId // Chat UI theme (defaults to 'theme-modern')
  speakingStyleId?: SpeakingStyleId // Reference to which preset was selected (for UI display)
  speakingStylePrompt?: string // The actual prompt text (stored/frozen when selected)
  ttsVoiceId?: TTSVoiceId // Selected TTS voice for the chatbot (defaults to 'voice-none' = disabled)
  timezone?: string // IANA timezone identifier (e.g., 'Asia/Tokyo', 'America/New_York')
  ownerId?: string // User ID of the owner (for future auth integration)
  serviceId?: string // ID of the published service (when status is 'published')
  // NEW: Short code system for stable URLs and fast KV-based lookups
  shortCode?: string // Stable 8-character identifier (e.g., "h3j7km2p") - primary chat URL identifier
  orgSlug?: string // Organization slug for services API (e.g., "akavirtual")
  serviceSlug?: string // Service slug for RAG API (typically = shortCode)
  lastPublishedAt?: string // Last time the venue was auto-published as a service (for RAG)
  lineIntegration?: LineIntegration // LINE Bot integration status
  createdAt: string
  updatedAt: string
}

// Zod schemas for validation
export const TimePeriodSchema = z.object({
  open: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  close: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
})

export const DayHoursSchema = z.object({
  day: z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']),
  isClosed: z.boolean(),
  periods: z.array(TimePeriodSchema),
})

export const VenueContactSchema = z.object({
  phone: z.string().optional(),
  email: z.string().email().or(z.literal('')).optional(),
  website: z.string().url().or(z.literal('')).optional(),
})

export const VenueAddressSchema = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
})

export const VenuePhotoSchema = z.object({
  id: z.string(),
  url: z.string().url(),
  filename: z.string(),
  caption: z.string().max(200, 'Caption too long').optional(),
  order: z.number(),
  hidden: z.boolean().optional(),
  uploadedAt: z.string(),
})

export const VenueDocumentSchema = z.object({
  id: z.string(),
  url: z.string().url(),
  filename: z.string(),
  fileType: z.enum(['pdf', 'txt', 'text', 'md', 'csv']),
  uploadedAt: z.string(),
  processed: z.boolean(),
})

export const VenueRagUrlSchema = z.object({
  id: z.string(),
  url: z.string().url('Invalid URL format'),
  title: z.string().max(200, 'Title too long').optional(),
  addedAt: z.string(),
  lastScrapedAt: z.string().optional(),
  status: z.enum(['pending', 'active', 'error']),
  errorMessage: z.string().optional(),
})

export const VenueSchema = z.object({
  id: z.string(),
  title: z.string().min(1, 'Title is required').max(100, 'Title too long'),
  description: z.string().max(2000, 'Description too long'),
  address: VenueAddressSchema,
  contact: VenueContactSchema,
  hours: z.array(DayHoursSchema),
  photos: z.array(VenuePhotoSchema),
  documents: z.array(VenueDocumentSchema),
  ragUrls: z.array(VenueRagUrlSchema).optional(),
  snsLinks: z.array(VenueSNSLinkSchema).optional(), // Social media links
  vanitySlug: z.string().regex(/^[a-z0-9-]*$/, 'Slug must be lowercase letters, numbers, and hyphens only'),
  status: VenueStatusSchema,
  themeId: ChatThemeIdSchema.optional(), // Chat UI theme
  speakingStyleId: SpeakingStyleIdSchema.optional(), // Reference to preset (for UI)
  speakingStylePrompt: z.string().max(2000, 'Speaking style prompt too long').optional(), // Actual prompt text
  ttsVoiceId: TTSVoiceIdSchema.optional(), // Selected TTS voice
  timezone: z.string().optional(), // IANA timezone identifier
  ownerId: z.string().optional(),
  serviceId: z.string().optional(), // ID of the published service
  // NEW: Short code system fields
  shortCode: z.string().optional(), // Stable 8-character identifier
  orgSlug: z.string().optional(), // Organization slug for services API
  serviceSlug: z.string().optional(), // Service slug for RAG API
  lastPublishedAt: z.string().optional(), // Last auto-publish timestamp
  createdAt: z.string(),
  updatedAt: z.string(),
})

// Form data for creating/editing venues (omits generated fields)
export const VenueFormSchema = VenueSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  photos: true, // Photos handled separately via upload
  documents: true, // Documents handled separately via upload
  ragUrls: true, // RAG URLs handled separately via validation API
  snsLinks: true, // SNS links handled separately via editor
})

export type VenueFormData = z.infer<typeof VenueFormSchema>

/**
 * Simplified schema for creating a new venue
 * Only requires the restaurant name - everything else is optional
 * and can be filled in after the initial creation
 */
export const NewVenueFormSchema = z.object({
  title: z.string().min(1, 'Restaurant name is required').max(100, 'Name too long'),
})

export type NewVenueFormData = z.infer<typeof NewVenueFormSchema>

// Helper to get status display info
export const getVenueStatusInfo = (status: VenueStatus) => {
  switch (status) {
    case 'draft':
      return { label: 'Draft', color: 'bg-yellow-100 text-yellow-800', description: 'Not visible to the public' }
    case 'published':
      return { label: 'Published', color: 'bg-green-100 text-green-800', description: 'Visible to the public' }
    case 'archived':
      return { label: 'Archived', color: 'bg-gray-100 text-gray-800', description: 'Hidden from public' }
  }
}
